import React from 'react';
import { formatTotalDuration } from '../../utils/dateHelpers';

const STAGE_COLOURS = {
  submitted: 'var(--cp-secondary)',
  in_progress: 'var(--cp-success)',
  paused: 'var(--cp-warning)',
  sent_to_sign: 'var(--cp-accent)',
  signed: 'var(--opt-sky)',
  completed: 'var(--cp-text)',
};

// Dark navy used for the tab in the fanned-archive stack and for the body
// of an expanded/hovered card. Matches the rgba(26,37,64,...) shadow tokens
// in the fanned-stack brief.
export const EXPANDED_BODY_BG = 'var(--cp-text)';

// Vertical clearance reserved ABOVE a card in a pipeline column.
//
// FolderCard draws its folder tab 14px above the card body (top: -14px), so
// the card needs room above itself or the tab collides with whatever sits
// there. That clearance is what sets where the card body starts in a column,
// and PrepSheetCard imports this SAME value so both card types begin at the
// same y — a prep card that is first in its column lines up with the file
// card that is first in the next one. One value, one source of truth: change
// it here and both card types move together.
export const CARD_TOP_CLEARANCE = '18px';

/**
 * FolderCard — the canonical file card used across every column.
 *
 * `expanded` toggles the "hovered/expanded" appearance used inside the
 * FannedStack: the body colour flips to dark navy while every other
 * element keeps its current position, size and relative styling. Text
 * and badge colours are re-tinted to stay WCAG AA on the darker body.
 *
 * `completedView` switches the card to the File-Completed-column
 * presentation: the right-side compact indicator slot is reserved for
 * total completion time (`completed_at − submitted_at`), and the URGENT
 * pill / notes badge are suppressed so the slot reads cleanly.
 */
export default function FolderCard({
  file,
  onClick,
  notesCount = 0,
  pendingAck = 0,
  expanded = false,
  completedView = false,
  hasOpenFlag = false,
  interactive = true,
}) {
  const colour = STAGE_COLOURS[file.status] || 'var(--cp-secondary)';

  // Card illumination (CLAUDE.md §31): OWNER-ONLY. `pendingAck` is the count
  // of unacknowledged notes routed to the VIEWER'S ACTIVE ROLE (scoped in
  // SharedPipeline), so the card lights and gently flashes only for the role
  // that must acknowledge — never for the author or third parties. Amber ring
  // (breathing pulse via .inbox-ack-lit) + pinging amber dot on the note
  // badge. Kept distinct from the stage colours and the red urgent/flag cues.
  const lit = pendingAck > 0;

  const bodyBg = expanded ? EXPANDED_BODY_BG : 'white';
  const clientNameColour = expanded ? '#FFFFFF' : '#0F4C75';
  const mutedColour = expanded ? 'rgba(255, 255, 255, 0.72)' : '#6B7A8D';
  const notesBadgeBg = expanded ? 'rgba(255, 255, 255, 0.14)' : 'rgba(15, 76, 117, 0.08)';
  const notesBadgeColour = expanded ? '#FFFFFF' : '#0F4C75';
  const urgentPillBg = expanded ? 'rgba(231, 111, 81, 0.28)' : 'rgba(192, 57, 43, 0.1)';
  const urgentPillColour = expanded ? '#FFD4C7' : '#C0392B';
  const daysColour = expanded ? '#FFFFFF' : colour;
  const dateColour = expanded ? 'rgba(255, 255, 255, 0.72)' : '#6B7A8D';

  return (
    <div
      onClick={onClick}
      style={{
        position: 'relative',
        marginTop: CARD_TOP_CLEARANCE,
        // App-level role gate (CLAUDE.md §30): a non-permitted role still sees
        // the card in full, but it drops the "openable" pointer cursor so it
        // reads as non-interactive (a subtle, light-touch cue — no dimming,
        // no lock badge). onClick still fires so the pipeline can toast.
        cursor: interactive ? 'pointer' : 'default',
      }}
    >
      {/* Folder tab — top left */}
      <div style={{
        position: 'absolute',
        top: '-14px',
        left: '16px',
        width: '64px',
        height: '14px',
        background: colour,
        borderRadius: '6px 6px 0 0',
      }} />
      {/* Card body — the .inbox-ack-lit class carries the breathing amber
          ring (box-shadow keyframes in globals.css; static under
          prefers-reduced-motion), so no inline boxShadow is set when lit. */}
      <div
        className={lit ? 'inbox-ack-lit' : undefined}
        // Return-to-dashboard lookup (CLAUDE.md §39): the card body carries
        // its record identity so the dashboard can scroll to and briefly
        // highlight the card the user just created or moved.
        data-card-type="file"
        data-card-id={file.id}
        style={{
        position: 'relative',
        background: bodyBg,
        // Test records (is_test = true, CLAUDE.md §33) get a dashed coral
        // border (existing --cp-warning token) so they can never be
        // mistaken for real client work. Acknowledgement illumination
        // still takes precedence when lit. Real records are unchanged.
        border: lit
          ? '1.5px solid var(--cp-ack-amber)'
          : file.isTest
            ? '2px dashed var(--cp-warning)'
            : `2px solid ${colour}`,
        borderRadius: '14px',
        padding: '12px',
        overflow: 'hidden',
      }}>
        {/* Thin colour strip inside top of card */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '5px',
          background: colour,
        }} />
        {/* Verification flag dot — appears only on signed files that
            have at least one case_screenshot with verification_status =
            'flagged'. Subtle 8px amber dot in the top-right corner so
            the advisor can scan the kanban for files needing review. */}
        {hasOpenFlag && (
          <span
            title="Verification flagged — review this file."
            style={{
              position: 'absolute',
              top: '8px',
              right: '8px',
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              background: 'var(--cp-accent)',
              boxShadow: '0 0 0 2px rgba(244, 162, 97, 0.25)',
              zIndex: 1,
            }}
          />
        )}
        {/* Notes count badge — sits unobtrusively in the top-right of the card.
            Suppressed in completedView so the indicator slot is reserved
            exclusively for total completion time on completed files. */}
        {notesCount > 0 && !completedView && (
          <span
            title={
              lit
                ? `${notesCount} note${notesCount === 1 ? '' : 's'} — ${pendingAck} awaiting acknowledgement`
                : `${notesCount} note${notesCount === 1 ? '' : 's'}`
            }
            style={{
              position: 'absolute',
              top: '10px',
              right: '10px',
              background: notesBadgeBg,
              color: notesBadgeColour,
              fontSize: '10px',
              fontWeight: 700,
              padding: '2px 7px',
              borderRadius: '999px',
              lineHeight: 1.4,
              letterSpacing: '0.02em',
            }}
          >
            {notesCount} {notesCount === 1 ? 'note' : 'notes'}
            {/* Pinging amber dot — an unacknowledged-note cue on the badge.
                The expanding ring uses the .inbox-ack-ping keyframe in
                globals.css (disabled under prefers-reduced-motion). */}
            {lit && (
              <span style={{ position: 'absolute', top: '-3px', right: '-3px', width: '8px', height: '8px' }}>
                <span
                  className="inbox-ack-ping"
                  style={{
                    position: 'absolute',
                    inset: 0,
                    borderRadius: '50%',
                    background: 'var(--cp-ack-amber)',
                  }}
                />
                <span
                  style={{
                    position: 'absolute',
                    inset: 0,
                    borderRadius: '50%',
                    background: 'var(--cp-ack-amber)',
                    boxShadow: '0 0 0 1.5px #fff',
                  }}
                />
              </span>
            )}
          </span>
        )}
        <div style={{ marginTop: '8px' }}>
          <div style={{ fontWeight: 700, fontSize: '14px', color: clientNameColour }}>
            {file.clientName}
          </div>
          <div style={{ fontSize: '12px', color: mutedColour, marginTop: '2px' }}>
            {file.workCategory}
          </div>
          {/* TEST badge — always shown on test records, in every column
              (including completedView), so a test file is unmistakable at a
              glance (CLAUDE.md §33). Uses only existing design tokens. */}
          {file.isTest && (
            <span style={{
              display: 'inline-block',
              marginTop: '4px',
              marginRight: '4px',
              padding: '2px 8px',
              borderRadius: '8px',
              background: 'var(--cp-surface-alt)',
              border: '1px dashed var(--cp-warning)',
              fontSize: '10px',
              fontWeight: 700,
              color: 'var(--cp-warning)',
              letterSpacing: '0.04em',
            }}>TEST</span>
          )}
          {file.priority === 'Urgent' && !completedView && (
            <span style={{
              display: 'inline-block',
              marginTop: '4px',
              padding: '2px 8px',
              borderRadius: '8px',
              background: urgentPillBg,
              fontSize: '10px',
              fontWeight: 700,
              color: urgentPillColour,
            }}>URGENT</span>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '10px' }}>
            <span style={{ fontSize: '11px', color: dateColour }}>
              {file.appointmentDate || file.submittedAt?.slice(0, 10)}
            </span>
            {completedView ? (
              <span style={{ fontSize: '12px', fontWeight: 600, color: daysColour }}>
                {formatTotalDuration(completionDurationMs(file))}
              </span>
            ) : (
              file.activeDaysUsed !== undefined && (
                <span style={{ fontSize: '12px', fontWeight: 600, color: daysColour }}>
                  {Number(file.activeDaysUsed).toFixed(1)}d
                </span>
              )
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Returns ms between submitted_at and completed_at, or null if either is
// missing — formatTotalDuration handles the null gracefully.
function completionDurationMs(file) {
  if (!file.completedAt || !file.submittedAt) return null;
  const start = new Date(file.submittedAt).getTime();
  const end = new Date(file.completedAt).getTime();
  if (Number.isNaN(start) || Number.isNaN(end)) return null;
  return end - start;
}
